import { ClientI18NProvider, useI18N } from '@susu/i18n/client'
import { Button, Checkbox, Loader } from '@susu/ui/components'
import { useEffect, useRef, useState } from 'react'
import {
  initializeAnalytics,
  type PreferenceCenterAnalyticsConfig,
  trackPreferencesSaved,
} from '../lib/analytics/analytics'
import { getAuthParamsFromUrl } from '../lib/auth'
import type { Environment } from '../lib/config'
import { readDomAttributes } from '../lib/dom-attributes'
import { resolvePageLanguage } from '../lib/locale-resolver'
import {
  fetchPreferences,
  updatePreferences,
  type UpdatePreferencesPayload,
} from '../lib/preferences.api'
import { translations } from '../locales'
import { LanguageDropdown } from './LanguageDropdown'

const BORDER_SELECTED = 'border-border-selected'
const BORDER_DEFAULT = 'border-border hover:border-border-hover'

export interface PreferenceCenterCoreProps {
  env: Environment
  executedBy: string
  onLanguageChange?: (language: string) => void
  onSave?: (preferences: { newsletterStatus: boolean }) => void
  onSaveSuccess?: () => void
  email?: string
  accountNumber?: string
  deliveryId?: string
  code?: string
  token?: string
  isStandalone?: boolean
  /**
   * Language to render the UI in, e.g. 'de'. Wins over the URL and over
   * <html lang>. Pass it from a host that already resolved its own locale so
   * the two never disagree. Unsupported values are ignored.
   */
  language?: string
  analyticsConfig?: PreferenceCenterAnalyticsConfig
}

/**
 * Internal component that uses i18n
 */
function PreferenceCenterContent({
  env: envProp,
  executedBy: executedByProp,
  onLanguageChange,
  onSave,
  onSaveSuccess,
  email: emailProp,
  accountNumber: accountNumberProp,
  deliveryId: deliveryIdProp,
  code: codeProp,
  token,
  isStandalone = true,
  analyticsConfig,
}: PreferenceCenterCoreProps) {
  // Priority: DOM attributes > props > URL query params
  const domAttrs = readDomAttributes()
  const urlParams = getAuthParamsFromUrl()

  const accountNumber = accountNumberProp
  const email = domAttrs.email || emailProp || urlParams.email
  const deliveryId =
    domAttrs.deliveryId || deliveryIdProp || urlParams.deliveryId
  const code = token ? null : domAttrs.code || codeProp || urlParams.code
  const env = domAttrs.env || envProp
  // Priority: DOM > URL query > required prop (always resolves to string)
  const executedBy: string =
    domAttrs.executedBy || urlParams.executedBy || executedByProp

  // The page locale, already resolved by the provider below. It only seeds the
  // dropdown — once preferences load, the dropdown holds the customer's stored
  // communication language and the page locale no longer follows it.
  const { t, language: pageLanguage } = useI18N()
  const [language, setLanguage] = useState(pageLanguage)
  const [newsletters, setNewsletters] = useState(false)
  const [curatedForYou, setCuratedForYou] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [apiAccountNumber, setApiAccountNumber] = useState<string>('')

  // Store callback in ref to avoid effect re-runs when callback changes
  const onLanguageChangeRef = useRef(onLanguageChange)
  useEffect(() => {
    onLanguageChangeRef.current = onLanguageChange
  }, [onLanguageChange])

  const datadogCleanupRef = useRef<(() => void) | null>(null)

  // Show error if both accountNumber and email are missing (must be after hooks)
  if (!accountNumber && !email) {
    return (
      <div
        className="flex min-h-screen w-full flex-col items-center justify-center gap-16 bg-background px-16"
        data-testid="preference-center-missing-params-error"
      >
        <h1 className="text-xl leading-xl font-medium text-text text-center">
          Failed to load preferences
        </h1>
        <p className="text-base leading-base font-light text-text-secondary text-center max-w-[400px]">
          Missing account number or email. Please provide via URL parameters or
          contact support.
        </p>
      </div>
    )
  }

  useEffect(() => {
    let cancelled = false

    // Initialize Segment and Avo Inspector; wire Datadog cleanup on unmount.
    initializeAnalytics(env, analyticsConfig)
      .then((cleanup) => {
        if (cancelled) {
          cleanup?.()
          return
        }
        datadogCleanupRef.current = cleanup ?? null
      })
      .catch((error) => {
        console.error('Failed to initialize Datadog RUM:', error)
      })

    const loadPreferences = async () => {
      if (!token && !code) {
        const errorMsg = 'Missing authentication: provide token or code'
        console.error(errorMsg)
        setLoadError(errorMsg)
        setLoading(false)
        return
      }

      try {
        // Use accountNumber if available, fallback to email
        const identifier = accountNumber || email || ''
        const isAccountNumber = !!accountNumber

        const preferences = await fetchPreferences(
          identifier,
          code || '',
          env,
          token,
          isAccountNumber,
        )

        // Store account number from response
        setApiAccountNumber(preferences.identity.accountNumber)

        // Set user's preferred communication language from API (not page translation)
        const preferredLanguage = preferences.language || 'en'
        setLanguage(preferredLanguage)

        setNewsletters(preferences.channels.email.newsletter)
        setCuratedForYou(preferences.channels.email.curatedForYou)
      } catch (error) {
        console.error('Failed to fetch preferences:', error)
        setLoadError('error.loadPreferences')
      } finally {
        setLoading(false)
      }
    }

    loadPreferences()

    return () => {
      cancelled = true
      datadogCleanupRef.current?.()
      datadogCleanupRef.current = null
    }
  }, [analyticsConfig, email, code, token, env])

  const handleSavePreferences = async () => {
    if (!token && !code) {
      console.error('Missing authentication: provide token or code')
      setSaveError('Missing authentication')
      return
    }

    setSaving(true)
    setSaveError(null)
    setSaveSuccess(false)

    try {
      const payload: UpdatePreferencesPayload = {
        identity: {
          email: email || '',
          accountNumber: accountNumber || apiAccountNumber,
        },
        code: code || '',
        language,
        channels: {
          email: {
            newsletter: newsletters,
            curatedForYou: curatedForYou,
          },
        },
        executedBy,
        ...(deliveryId && { deliveryId }),
      }

      const updatedPreferences = await updatePreferences(payload, env, token)

      // Update internal state with response
      setApiAccountNumber(updatedPreferences.identity.accountNumber)
      const updatedLanguage = updatedPreferences.language || 'en'
      setLanguage(updatedLanguage)
      onLanguageChangeRef.current?.(updatedLanguage)
      setNewsletters(updatedPreferences.channels.email.newsletter)
      setCuratedForYou(updatedPreferences.channels.email.curatedForYou)

      // Track successful save
      trackPreferencesSaved(updatedLanguage, newsletters, curatedForYou)
      onSave?.({
        newsletterStatus: updatedPreferences.channels.email.newsletter,
      })

      // Standalone keeps the inline success banner; the Customer Hub panel
      // closes instead, matching the other hub panels (Customer info / Address
      // book), so it shows no green banner.
      if (isStandalone) {
        setSaveSuccess(true)

        // Auto-dismiss after 5 seconds
        setTimeout(() => {
          setSaveSuccess(false)
        }, 5000)
      } else {
        onSaveSuccess?.()
      }
    } catch (error) {
      console.error('Failed to save preferences:', error)
      setSaveError('error.savePreferences')
    } finally {
      setSaving(false)
    }
  }

  const handleLanguageChange = (newLanguage: string) => {
    // This updates the user's preferred communication language, not page translation
    setLanguage(newLanguage)
  }

  if (loading) {
    return (
      <div
        className="flex flex-col gap-16 h-full items-center justify-center px-16 py-24"
        data-testid="preference-center-loading"
      >
        <Loader />
      </div>
    )
  }

  if (loadError) {
    return (
      <div
        className="flex min-h-screen w-full flex-col items-center justify-center gap-16 bg-background px-16"
        data-testid="preference-center-load-error"
      >
        <h1 className="text-xl leading-xl font-medium text-text text-center">
          {t(loadError)}
        </h1>
      </div>
    )
  }

  return (
    <form
      className="flex flex-col pt-20 h-full"
      onSubmit={handleSavePreferences}
      data-testid="preference-center-form"
    >
      <div className="flex flex-1 flex-col items-center px-16 lg:px-20 3xl:px-120 overflow-y-auto">
        <div className="w-full flex flex-col gap-40 pt-20 pb-60">
          {isStandalone && (
            <h1 className="text-2xl leading-2xl font-medium text-text text-center max-w-[280px] mx-auto">
              {t('header.title')}
            </h1>
          )}
          <div
            className="w-full"
            data-testid="preference-center-language-section"
          >
            <h2 className="text-base leading-base font-medium text-text mb-4">
              {t('sections.language.title')}
            </h2>
            <p className="text-base leading-base font-light text-text mb-20">
              {t('sections.language.description')}
            </p>

            <LanguageDropdown
              value={language}
              onChange={handleLanguageChange}
              env={env}
              data-testid="preference-center-language-dropdown"
            />
          </div>

          <div className="w-full" data-testid="preference-center-email-section">
            <h2 className="text-base leading-base font-medium text-text mb-4">
              {t('sections.email.title')}
            </h2>
            <p className="text-base leading-base font-light text-text mb-20">
              {t('sections.email.description')}
            </p>

            <div className="flex flex-col gap-12">
              <div
                className={`relative flex items-center justify-between border rounded-md p-[19px] transition-colors ${newsletters ? BORDER_SELECTED : BORDER_DEFAULT}`}
                data-testid="preference-center-newsletter-card"
              >
                <div className="flex flex-col gap-4">
                  <h3 className="text-base leading-base font-medium text-text">
                    {t('preferences.email.newsletters.label')}
                  </h3>
                  <p className="text-sm leading-sm font-light text-text">
                    {t('preferences.email.newsletters.description')}
                  </p>
                </div>
                <Checkbox
                  checked={newsletters}
                  expandClickArea={true}
                  onChange={(e) => setNewsletters(e.target.checked)}
                  data-testid="preference-center-newsletter-checkbox"
                />
              </div>

              <div
                className={`relative flex items-center justify-between border rounded-md p-[19px] transition-colors ${curatedForYou ? BORDER_SELECTED : BORDER_DEFAULT}`}
                data-testid="preference-center-curated-card"
              >
                <div className="flex flex-col gap-4">
                  <h3 className="text-base leading-base font-medium text-text">
                    {t('preferences.email.curatedForYou.label')}
                  </h3>
                  <p className="text-sm leading-sm font-light text-text">
                    {t('preferences.email.curatedForYou.description')}
                  </p>
                </div>
                <Checkbox
                  checked={curatedForYou}
                  expandClickArea={true}
                  onChange={(e) => setCuratedForYou(e.target.checked)}
                  data-testid="preference-center-curated-checkbox"
                />
              </div>
            </div>
          </div>

          <p
            className="text-xs leading-xs font-light text-text-secondary text-center max-w-[246px] mx-auto"
            data-testid="preference-center-disclaimer"
          >
            {t('footer.disclaimerBefore')}{' '}
            <a
              href="https://suitsupply.com/privacy-statement.html"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
              data-testid="preference-center-privacy-link"
            >
              {t('footer.privacyPolicy')}
            </a>
            {t('footer.disclaimerAfter')}
          </p>
        </div>
      </div>

      <div className="border-t border-border p-16 lg:p-20 flex flex-col gap-12">
        {saveSuccess && (
          <div
            className="text-sm text-text-success text-center py-12 px-16 bg-surface-success rounded-md"
            data-testid="preference-center-save-success"
          >
            {t('success.savePreferences')}
          </div>
        )}
        {saveError && (
          <div
            className="text-sm text-text-error text-center py-12 px-16 bg-surface-error rounded-md"
            data-testid="preference-center-save-error"
          >
            {t(saveError)}
          </div>
        )}
        <Button
          size="l"
          variant="filled"
          onClick={handleSavePreferences}
          disabled={saving}
          className="w-full"
          data-testid="preference-center-save-button"
        >
          {saving ? <Loader /> : t('actions.savePreferences')}
        </Button>
      </div>
    </form>
  )
}

/**
 * Core PreferenceCenter component that accepts props
 * This is the main component for use in the monorepo
 * Includes i18n provider internally
 */
export default function PreferenceCenterCore(props: PreferenceCenterCoreProps) {
  // Same chain standalone and embedded: host prop > ?language > URL locale >
  // <html lang> > English.
  const pageLanguage = resolvePageLanguage(props.language) || 'en'

  return (
    <ClientI18NProvider language={pageLanguage} messages={translations}>
      <PreferenceCenterContent {...props} />
    </ClientI18NProvider>
  )
}
